import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/context/AuthContext'

/** Best effort: the result is not needed, and a failure changes nothing on screen. */
const noop = () => {}

/**
 * "Seen", typing and online status for the signed-in chats
 * (20260930140000_chat_presence.sql). No Realtime presence channels: each
 * signal is a row RLS already guards, so nothing here needs policies on the
 * realtime schema.
 */

/** Someone counts as online while their heartbeat is this fresh. */
export const ONLINE_WINDOW_MS = 2 * 60_000
/** How long one typing ping lasts; the composer renews it well inside this. */
const TYPING_TTL_MS = 6000
const TYPING_PING_MS = 3000

export function isOnline(lastSeenAt: string | null | undefined, now = Date.now()): boolean {
  return !!lastSeenAt && now - new Date(lastSeenAt).getTime() < ONLINE_WINDOW_MS
}

/** The other members' read positions. Polled; nobody's read is pushed. */
export function useThreadReadStates(threadId: string | undefined) {
  return useQuery({
    queryKey: ['thread-reads', threadId],
    enabled: !!threadId,
    refetchInterval: 20_000,
    queryFn: async (): Promise<{ userId: string; lastReadAt: string }[]> => {
      const { data, error } = await supabase.rpc('thread_read_states', { _thread: threadId as string })
      if (error) throw error
      return (data ?? []).map(r => ({ userId: r.user_id, lastReadAt: r.last_read_at }))
    },
  })
}

export function useThreadPresence(threadId: string | undefined) {
  return useQuery({
    queryKey: ['thread-presence', threadId],
    enabled: !!threadId,
    refetchInterval: 30_000,
    queryFn: async (): Promise<{ userId: string; kind: 'staff' | 'client'; lastSeenAt: string | null }[]> => {
      const { data, error } = await supabase.rpc('thread_presence', { _thread: threadId as string })
      if (error) throw error
      return (data ?? []).map(r => ({
        userId: r.user_id,
        kind: r.kind === 'client' ? 'client' : 'staff',
        lastSeenAt: r.last_seen_at,
      }))
    },
  })
}

/** Staff last-seen for the conversation list's dots. Staff can read staff profiles directly. */
export function useStaffLastSeen(ids: string[]) {
  const key = [...ids].sort()
  return useQuery({
    queryKey: ['staff-last-seen', ...key],
    enabled: key.length > 0,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Map<string, string | null>> => {
      const { data, error } = await supabase.from('profiles').select('id, last_seen_at').in('id', key)
      if (error) throw error
      return new Map((data ?? []).map(r => [r.id, r.last_seen_at]))
    },
  })
}

/** The minute heartbeat that makes last_seen_at mean "has the app open". */
export function useLastSeenHeartbeat() {
  const { user } = useAuth()
  useEffect(() => {
    if (!user) return
    const beat = () => {
      // .then(): a supabase query is sent only when awaited or then-ed, and
      // `void` alone left the heartbeat unsent (found 30 October 2026).
      if (document.visibilityState === 'visible') supabase.rpc('touch_last_seen').then(noop)
    }
    beat()
    const timer = window.setInterval(beat, 60_000)
    document.addEventListener('visibilitychange', beat)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', beat)
    }
  }, [user])
}

/**
 * Typing in one thread: who else is typing (live, from chat_typing), and
 * `notify` / `stop` for the composer. Pings are throttled so a fast typist
 * costs one write every few seconds, and `stop` runs on send and on leaving.
 */
export function useTyping(threadId: string | undefined) {
  const { user } = useAuth()
  const [until, setUntil] = useState<Map<string, number>>(new Map())
  const [now, setNow] = useState(() => Date.now())
  const lastPing = useRef(0)

  useEffect(() => {
    setUntil(new Map())
    if (!threadId || !user) return
    let cancelled = false

    const apply = (row: { user_id: string; until: string } | null, removed = false) => {
      if (!row || row.user_id === user.id) return
      setUntil(prev => {
        const next = new Map(prev)
        if (removed) next.delete(row.user_id)
        else next.set(row.user_id, new Date(row.until).getTime())
        return next
      })
    }

    void supabase
      .from('chat_typing')
      .select('user_id, until')
      .eq('thread_id', threadId)
      .then(({ data }) => {
        if (!cancelled) for (const row of data ?? []) apply(row)
      })

    const channel = supabase
      .channel(`typing-${threadId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_typing', filter: `thread_id=eq.${threadId}` },
        payload => {
          if (payload.eventType === 'DELETE') apply(payload.old as { user_id: string; until: string }, true)
          else apply(payload.new as { user_id: string; until: string })
        }
      )
      .subscribe()

    return () => {
      cancelled = true
      void supabase.removeChannel(channel)
    }
  }, [threadId, user])

  // Expire indicators without waiting for a DELETE that may never come.
  const anyLive = [...until.values()].some(t => t > now)
  useEffect(() => {
    if (!anyLive) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [anyLive])

  const notify = useCallback(() => {
    if (!threadId || !user) return
    const t = Date.now()
    if (t - lastPing.current < TYPING_PING_MS) return
    lastPing.current = t
    // .then() sends it; see useLastSeenHeartbeat.
    supabase
      .from('chat_typing')
      .upsert(
        { thread_id: threadId, user_id: user.id, until: new Date(t + TYPING_TTL_MS).toISOString() },
        { onConflict: 'thread_id,user_id' }
      )
      .then(noop)
  }, [threadId, user])

  const stop = useCallback(() => {
    if (!threadId || !user || lastPing.current === 0) return
    lastPing.current = 0
    supabase.from('chat_typing').delete().eq('thread_id', threadId).eq('user_id', user.id).then(noop)
  }, [threadId, user])

  // Leaving the thread mid-sentence.
  useEffect(() => () => stop(), [stop])

  const typingUserIds = [...until.entries()].filter(([, t]) => t > now).map(([id]) => id)
  return { typingUserIds, notify, stop }
}
